import type { ReactNode } from "react";

import { Field, FieldDescription, FieldError, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import type { FieldState } from "./billing-validation";

/**
 * The fields every billing form is built from (ADR-0073). Each one knows its requirement and shows
 * it once an attempt has failed, under the error if there is one, so an employee correcting a
 * form sees what every field wants without guessing. The forms disable the browser's own
 * validation bubbles for the same reason: one voice, in the platform's words.
 */

const SELECT_CLASS =
  "h-10 w-full rounded-sm border border-input bg-card px-3 text-sm aria-invalid:border-destructive";

type FieldProps = FieldState & { label: string; className?: string };

function describedBy(state: FieldState): string | undefined {
  const ids = [
    state.error === undefined ? null : `${state.id}-error`,
    state.showHint && state.hint !== undefined && state.hint !== state.error
      ? `${state.id}-hint`
      : null,
  ].filter((id): id is string => id !== null);
  return ids.length === 0 ? undefined : ids.join(" ");
}

export function FieldShell({ children, ...state }: FieldProps & { children: ReactNode }) {
  const { id, label, hint, error, showHint, className } = state;
  return (
    <Field className={className} data-invalid={error === undefined ? undefined : true}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      {children}
      {error === undefined ? null : <FieldError id={`${id}-error`}>{error}</FieldError>}
      {showHint && hint !== undefined && hint !== error ? (
        <FieldDescription id={`${id}-hint`}>{hint}</FieldDescription>
      ) : null}
    </Field>
  );
}

export function TextField({
  value,
  onChange,
  type = "text",
  inputMode,
  maxLength,
  required,
  multiline = false,
  ...state
}: FieldProps & {
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "email" | "date";
  inputMode?: "decimal" | "numeric";
  maxLength?: number;
  required?: boolean;
  multiline?: boolean;
}) {
  const shared = {
    id: state.id,
    value,
    required,
    maxLength,
    "aria-invalid": state.error === undefined ? undefined : true,
    "aria-describedby": describedBy(state),
  };
  return (
    <FieldShell {...state}>
      {multiline ? (
        <Textarea
          {...shared}
          onChange={(event) => {
            onChange(event.currentTarget.value);
          }}
        />
      ) : (
        <Input
          {...shared}
          type={type}
          inputMode={inputMode}
          onChange={(event) => {
            onChange(event.currentTarget.value);
          }}
        />
      )}
    </FieldShell>
  );
}

/** A yes-or-no choice with its consequence spelled out beside it. It cannot be invalid. */
export function CheckboxField({
  id,
  label,
  hint,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <Field orientation="horizontal">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        aria-describedby={hint === undefined ? undefined : `${id}-hint`}
        onChange={(event) => {
          onChange(event.currentTarget.checked);
        }}
      />
      <div className="grid gap-1">
        <FieldLabel htmlFor={id}>{label}</FieldLabel>
        {hint === undefined ? null : <FieldDescription id={`${id}-hint`}>{hint}</FieldDescription>}
      </div>
    </Field>
  );
}

export function SelectField({
  value,
  onChange,
  options,
  disabled,
  ...state
}: FieldProps & {
  value: string;
  onChange: (value: string) => void;
  options: readonly { value: string; label: string }[];
  disabled?: boolean;
}) {
  return (
    <FieldShell {...state}>
      <select
        className={SELECT_CLASS}
        id={state.id}
        value={value}
        disabled={disabled}
        aria-invalid={state.error === undefined ? undefined : true}
        aria-describedby={describedBy(state)}
        onChange={(event) => {
          onChange(event.currentTarget.value);
        }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}
